import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/cn";

// Figma DS button / Size=Small: 22px tall, 8px horizontal padding, Body XXS Medium.
// The 22px height is also pinned via inline style below; variant classes set their
// own h-*, so we force it with !important here too. The duplication is intentional
// (it wins the override war), not a copy-paste slip.
const XS_FIXED_HEIGHT_CLASSES = "!h-[22px] !min-h-[22px] !max-h-[22px]";
const XS_PILL_SIZE_CLASSES = [
  XS_FIXED_HEIGHT_CLASSES,
  "!px-2 !py-0.5 !pt-0.5 !pb-0.5 gap-0.5",
  "!type-body-xxs-medium rounded-full box-border",
].join(" ");

const PILL_VARIANTS = [
  "default",
  "destructive",
  "secondary",
  "subtle",
  "gradient",
] as const;

// Common height constraints for fixed-height button variants
const FIXED_HEIGHT_CLASSES =
  "h-[32px] min-h-[32px] max-h-[32px] [&]:h-[32px] [&]:min-h-[32px] [&]:max-h-[32px]";
const FIXED_SIZE_CLASSES = "px-3 py-0 pt-0 pb-0 box-border";

// Common typography for fixed-height variants
const FIXED_TYPOGRAPHY = "type-body-s-regular";

// Common focus styles
const FOCUS_STYLES =
  "focus-visible:border-[var(--focus-border)] focus-visible:ring-0";

// Common disabled styles for light mode
const DISABLED_LIGHT =
  "disabled:bg-[#f5f5f5] disabled:text-[#a6a6a6] disabled:border disabled:!border-[var(--color-border)]";

// Common disabled styles for dark mode
const DISABLED_DARK =
  "dark:disabled:bg-[#313435] dark:disabled:text-[#868686] dark:disabled:border dark:disabled:!border-[var(--color-border)]";

const buttonVariants = cva(
  // Base styles - layout, typography, transitions, and accessibility
  [
    "inline-flex items-center justify-center",
    "whitespace-nowrap rounded-md type-body-s-regular",
    "duration-0 delay-0 transition-colors",
    "ring-offset-white dark:ring-offset-neutral-950",
    "focus-visible:outline-hidden",
    "disabled:pointer-events-none disabled:opacity-50",
    "cursor-pointer",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          // Background and text colors
          "bg-[#313435] text-white hover:bg-[#161718]",
          "dark:bg-[#f5f5f5] dark:text-black dark:hover:bg-[#ffffff] dark:hover:text-black",
          // Size and shape
          "rounded-full",
          FIXED_HEIGHT_CLASSES,
          FIXED_SIZE_CLASSES,
          FIXED_TYPOGRAPHY,
          // Border and focus
          "border-2 border-transparent dark:hover:border-transparent",
          FOCUS_STYLES,
          // Disabled states
          DISABLED_LIGHT,
          DISABLED_DARK,
          "disabled:opacity-100",
        ].join(" "),
        destructive: [
          // Background and text colors
          "bg-red-500 text-neutral-50 hover:bg-red-600",
          "dark:bg-red-900 dark:text-neutral-50 dark:hover:bg-red-800",
          // Size and shape
          "rounded-full",
          FIXED_HEIGHT_CLASSES,
          FIXED_SIZE_CLASSES,
          // Border and focus
          "border-2 border-transparent",
          FOCUS_STYLES,
        ].join(" "),
        secondary: [
          // Background and text colors
          "bg-transparent text-[#202122] hover:bg-[#e8e8e8] hover:opacity-100",
          "dark:bg-transparent dark:text-white dark:hover:bg-[#313435] dark:hover:text-white dark:hover:opacity-100",
          // Size and shape
          "rounded-full",
          FIXED_HEIGHT_CLASSES,
          FIXED_SIZE_CLASSES,
          // Border
          "border border-[var(--color-border)] hover:border-[var(--color-border)]",
          // Focus
          "focus-visible:border-2",
          FOCUS_STYLES,
          // Disabled states
          "disabled:bg-transparent disabled:text-[#a6a6a6] disabled:border disabled:!border-[var(--color-border)] disabled:opacity-100",
          "dark:disabled:bg-transparent dark:disabled:text-[#868686] dark:disabled:border dark:disabled:!border-[var(--color-border)]",
        ].join(" "),
        subtle: [
          // Text colors
          "text-[#202122] hover:text-[#202122]",
          "dark:text-white",
          // Border
          "border border-transparent hover:border-[var(--color-border)]",
          // Size and shape
          "rounded-full",
          FIXED_HEIGHT_CLASSES,
          FIXED_SIZE_CLASSES,
          // Focus - ensure border is 2px with correct colors
          "focus-visible:!border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0",
          // Disabled states
          "disabled:bg-transparent disabled:text-[#a6a6a6] dark:disabled:text-[#868686] disabled:opacity-100",
        ].join(" "),
        "header-icon": [
          "shrink-0 rounded-lg bg-transparent p-0",
          "text-neutral-600 dark:text-neutral-400",
          "hover:bg-black/[0.04] dark:hover:bg-white/[0.04]",
          "focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]",
        ].join(" "),
        inline: [
          // Minimal variant for inline contexts (e.g. table headers) with no
          // pill shape, fixed height, or hover border — just text + focus ring.
          "text-inherit hover:text-[#202122]",
          "dark:hover:text-white",
          // No background, border, or padding by default
          "bg-transparent border-2 border-transparent rounded",
          // Focus
          "focus-visible:border-[var(--focus-border)] focus-visible:ring-0",
          // Disabled
          "disabled:text-[#a6a6a6] dark:disabled:text-[#868686] disabled:opacity-100",
        ].join(" "),
        gradient: [
          // Background gradient
          "relative bg-gradient-to-r from-[#cceeff] to-[#f3fc50]",
          "hover:from-[#aee3fd] hover:to-[#f0fb29]",
          "dark:from-[#cceeff] dark:to-[#f3fc50] dark:hover:from-[#e3f6ff] dark:hover:to-[#fafeb4]",
          // Text color
          "text-[#202122]",
          // Size and shape
          "rounded-full",
          FIXED_HEIGHT_CLASSES,
          FIXED_SIZE_CLASSES,
          // Border
          "border border-[var(--color-border)]",
          // Focus
          "focus-visible:border-2",
          FOCUS_STYLES,
          "focus-visible:after:absolute focus-visible:after:inset-0 focus-visible:after:bg-white/20 focus-visible:after:rounded-full",
          // Disabled states
          "disabled:from-[#e9f7fe] disabled:to-[#fcfed7] dark:disabled:from-[#24495b] dark:disabled:to-[#36380b]",
          "disabled:text-[#a6a6a6] dark:disabled:text-[#868686]",
          "disabled:border disabled:border-[var(--color-border)] disabled:opacity-100",
          // Content positioning
          "[&>*]:relative [&>*]:z-10",
        ].join(" "),
      },
      size: {
        default: "",
        sm: "", // Backward compatibility — same as default
        xs: "",
        lg: "", // Backward compatibility — same as default
        icon: "", // Backward compatibility — same as default
      },
    },
    compoundVariants: [
      ...PILL_VARIANTS.map((variant) => ({
        variant,
        size: "xs" as const,
        class: XS_PILL_SIZE_CLASSES,
      })),
      {
        variant: "default",
        size: "xs",
        class: "enabled:!border-0",
      },
      {
        variant: "secondary",
        size: "xs",
        class: [
          "!border !border-black/[0.24] dark:!border-white/[0.24]",
          "hover:!border-black/[0.24] dark:hover:!border-white/[0.24]",
          "text-[#1d1e20] dark:text-white",
        ].join(" "),
      },
      {
        variant: "subtle",
        size: "xs",
        class:
          "!border !border-transparent hover:!border-[var(--color-border)]",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * SVG icon component rendered alongside the label. Accepts Lucide icons as
   * well as other icon components (e.g. simple-icons brand logos) that take
   * `className` and `size` props.
   */
  icon?: React.ComponentType<{ className?: string; size?: number | string }>;
  iconPosition?: "left" | "right";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      icon: Icon,
      iconPosition = "left",
      children,
      style,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const isXs = size === "xs";
    const isPillVariant =
      variant != null && (PILL_VARIANTS as readonly string[]).includes(variant);
    const isXsPill = isXs && isPillVariant;
    const needsDefaultFixedHeight = !isXs && isPillVariant;

    const iconSize = isXs ? 12 : 16;
    const hasIcon = !!Icon;

    // Check if children is just text (string or number) for alignment adjustment
    // Only apply to buttons without icons to align text-only buttons with icon+text buttons
    const isTextOnly =
      !hasIcon &&
      (typeof children === "string" || typeof children === "number");

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          // Adjust padding when icon is present for better visual balance
          !isXs && hasIcon && iconPosition === "left" && "!pl-2.5 !pr-3",
          !isXs && hasIcon && iconPosition === "right" && "!pl-3 !pr-2.5",
        )}
        style={
          isXsPill
            ? {
                height: "22px",
                minHeight: "22px",
                maxHeight: "22px",
                ...style,
              }
            : needsDefaultFixedHeight
            ? {
                height: "32px",
                minHeight: "32px",
                maxHeight: "32px",
                ...style,
              }
            : style
        }
        ref={ref}
        {...props}
      >
        {Icon && iconPosition === "left" && (
          <Icon
            className={cn("shrink-0", isXs ? "" : "mr-1")}
            size={iconSize}
          />
        )}
        {isTextOnly ? (
          <span
            className={cn(
              "inline-flex items-center",
              isXs ? "leading-[12px]" : "leading-[1]",
            )}
          >
            {children}
          </span>
        ) : (
          <Slottable>{children}</Slottable>
        )}
        {Icon && iconPosition === "right" && (
          <Icon
            className={cn("shrink-0", isXs ? "" : "ml-1")}
            size={iconSize}
          />
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
